import { useCallback, useEffect, useRef, useState } from "react";
import client from "../api/client";

/**
 * Paged image loading with infinite scroll, so a page view costs one page of
 * JSON rather than the whole library.
 */
export default function useImageCatalog({
  category = "all",
  search = "",
  favoritesOnly = false,
  sort = "newest",
  pageSize = 40,
}) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  // Guards against a stale response overwriting a newer one when the user
  // changes filters faster than the network responds.
  const requestId = useRef(0);

  const fetchPage = useCallback(
    async (targetPage, { append }) => {
      const id = ++requestId.current;
      append ? setLoadingMore(true) : setLoading(true);
      setError("");

      try {
        const { data } = await client.get("/api/images", {
          params: {
            category,
            search: search || undefined,
            favorites: favoritesOnly ? "true" : undefined,
            sort,
            page: targetPage,
            page_size: pageSize,
          },
        });

        if (id !== requestId.current) return;

        setItems((current) =>
          append ? [...current, ...data.items] : data.items
        );
        setPage(data.page);
        setTotal(data.total);
        setHasMore(data.has_more);
      } catch (err) {
        if (id !== requestId.current) return;
        console.error("Image fetch failed:", err);
        setError("Could not load artwork. Please try again.");
        if (!append) setItems([]);
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [category, search, favoritesOnly, sort, pageSize]
  );

  useEffect(() => {
    fetchPage(1, { append: false });
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchPage(page + 1, { append: true });
  }, [fetchPage, page, hasMore, loading, loadingMore]);

  /** Patch one item in place, e.g. after toggling a favourite. */
  const updateItem = useCallback((fileId, changes) => {
    setItems((current) =>
      current.map((item) =>
        item.file_id === fileId ? { ...item, ...changes } : item
      )
    );
  }, []);

  /** Drop one item, used when unfavouriting inside the favourites view. */
  const removeItem = useCallback((fileId) => {
    setItems((current) => current.filter((item) => item.file_id !== fileId));
    setTotal((current) => Math.max(0, current - 1));
  }, []);

  return {
    items,
    total,
    hasMore,
    loading,
    loadingMore,
    error,
    loadMore,
    updateItem,
    removeItem,
    reload: () => fetchPage(1, { append: false }),
  };
}
